import React from 'react';
import { Link } from 'react-router-dom';
import '../../../global.css';
import Navbar from '../../../components/Navbar';
import SiteFooter from '../../../components/SiteFooter';
import NeuralCanvas from '../../../components/NeuralCanvas';
import { useSEO } from '../../../hooks/useSEO';
import safetyHero from '../../../assets/images/healthcare.jpeg'; // Import your local image

export default function Health() {
  useSEO({
    title: 'Healthcare AI for Africa',
    description: 'AI trained on African patient populations, African disease patterns, and African clinical realities — helping doctors get the right diagnosis and saving lives across the continent.',
    path: '/solutions/health',
  });
  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '80px 0 72px' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>SOLUTION · HEALTH</div>
          <div className="sol-hero-grid">
            <div>
              <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 400, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 20 }}>
                Helping doctors<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>get the right diagnosis.</span>
              </h1>
              <p style={{ fontSize: 16, color: 'var(--fg-mid)', lineHeight: 1.7, maxWidth: 480, marginBottom: 32 }}>
                AI tools misdiagnosed African women of breast cancer — because they were trained on the wrong populations.
                Cybernate is built on African patient realities: our diseases, our bodies, our clinical settings, our languages.
                Doctors get it right.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/signup" className="btn btn-primary">Get started →</Link>
                <Link to="/developers" className="btn btn-ghost">Developer platform</Link>
              </div>
            </div>
            <div style={{ width: '100%', height: 'clamp(260px, 40vw, 480px)', borderRadius: 'var(--r-lg)', overflow: 'hidden', border: '1px solid var(--line)' }}>
              <img
                src={safetyHero}
                alt="African healthcare — doctors and patients in a clinical setting"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--line)', padding: '40px 0', background: 'var(--bg-1)' }}>
        <div className="sol-stats-grid" style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          {[['34%', 'Improvement over Western-only models'], ['18', 'Languages for patient intake'], ['HIPAA+', 'African compliance standards'], ['Low', 'Bandwidth mode for rural clinics']].map(([n, l], i) => (
            <div key={n} style={{ padding: '24px 32px', borderRight: i < 3 ? '1px solid var(--line)' : 'none' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 6 }}>{n}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          PHOTO SECTION — uncomment when image is ready.
          Replace src with your Unsplash URL.
          Tip: append ?w=1400&q=85&fit=crop to any Unsplash URL.
          ═══════════════════════════════════════════════════ */}
      {/*
      <div style={{ padding: '56px 0 0' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{
            width: '100%',
            height: 'clamp(220px, 42vw, 480px)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden',
            border: '1px solid var(--line)',
            background: 'var(--bg-1)',
          }}>
            <img
              src="YOUR_IMAGE_URL_HERE"
              alt="African healthcare — doctors and patients in a clinical setting"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
      */}

      <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '72px var(--pad-x)' }}>
        <h2 style={{ fontSize: 32, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 48 }}>Built for African<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>clinical realities.</span></h2>
        <div className="sol-features-grid">
          {[
            { title: 'Clinical documentation', desc: 'Transcribe consultations from voice notes in any African language. Generate SOAP notes, discharge summaries, and referral letters automatically.' },
            { title: 'Differential diagnosis', desc: 'AI-assisted differential diagnosis trained on African disease patterns — malaria, typhoid, TB, sickle cell — not just Western clinical datasets.' },
            { title: 'Patient intake', desc: 'Multilingual chatbot for patient registration, symptom collection, and triage. Works on USSD and WhatsApp for low-tech patients.' },
            { title: 'Drug information', desc: 'Real-time drug interaction and dosing support, calibrated for drugs available in African formularies and generic equivalents.' },
            { title: 'Medical imaging', desc: 'Malaria parasite detection from microscopy images. Chest X-ray analysis for TB. Vision models fine-tuned on African patient scans.' },
            { title: 'Community health workers', desc: 'Give CHWs an AI guide for home visits. Offline-capable, works in Hausa and Swahili, structured around WHO protocols.' },
          ].map(u => (
            <div key={u.title} style={{ padding: '24px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg)', marginBottom: 8 }}>{u.title}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.65 }}>{u.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--line)', padding: '64px 0', background: 'var(--bg-1)' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '0 var(--pad-x)' }}>
          <h2 style={{ fontSize: 28, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12 }}>Better health outcomes for 1.4 billion.</h2>
          <p style={{ fontSize: 14, color: 'var(--fg-mid)', marginBottom: 28 }}>Free to start. No credit card required. Works in 18 African languages.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to="/signup" className="btn btn-primary btn-lg">Get started →</Link>
            <Link to="/developers" className="btn btn-ghost btn-lg">Developer docs</Link>
          </div>
        </div>
      </div>

      <SiteFooter />
      </div>
    </>
  );
}
